import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const BG_URL = "https://static.prod-images.emergentagent.com/jobs/851f9404-6913-45b2-b2db-9428761c7aee/images/e1bfb9929d9c7cb408055172a88690bd0f48396d6a68ac378b78b18ad0ee361e.png";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, microsoftLogin } = useAuth();
  const [email, setEmail] = useState("admin@organogram.app");
  const [password, setPassword] = useState("Admin@12345");
  const [loading, setLoading] = useState(false);
  const [ssoDialog, setSsoDialog] = useState(false);
  const [ssoEmail, setSsoEmail] = useState("admin@organogram.app");

  const from = location.state?.from?.pathname || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  const submitMicrosoft = async () => {
    setSsoDialog(false);
    setLoading(true);
    const res = await microsoftLogin(ssoEmail);
    setLoading(false);
    if (res.ok) {
      toast.success("Signed in via Microsoft (mock)");
      navigate(from, { replace: true });
    } else {
      toast.error(res.error || "Microsoft sign-in denied");
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F4F4F2]" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      {/* Left visual panel */}
      <div className="relative hidden flex-1 overflow-hidden bg-neutral-900 lg:block">
        <img src={BG_URL} alt="" className="absolute inset-0 h-full w-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/20 to-black/70" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 bg-white" />
            <span className="text-sm font-semibold tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
              ORGANOGRAM
            </span>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-white/60" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
              Architecture of People
            </p>
            <h1 className="mt-3 max-w-md text-4xl font-medium leading-tight tracking-tight md:text-5xl" style={{ fontFamily: '"Work Sans", sans-serif' }}>
              Map your organization. Sync with Microsoft. Move with intent.
            </h1>
            <p className="mt-6 max-w-md text-sm text-white/70">
              Build, edit, and visualize your org structure in real time — from a Microsoft 365 import or a blank canvas.
            </p>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex w-full items-center justify-center px-8 lg:w-[480px]">
        <div className="w-full max-w-sm">
          <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            Sign In
          </p>
          <h2 className="mt-2 text-3xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
            Welcome back.
          </h2>
          <p className="mt-1 text-sm text-neutral-600">Continue to your organization.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-none border-neutral-300 focus:border-[#002FA7] focus:ring-[#002FA7]"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-none border-neutral-300 focus:border-[#002FA7]"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="h-11 w-full rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-neutral-200" />
            <span className="text-[10px] uppercase tracking-wider text-neutral-500">OR</span>
            <div className="h-px flex-1 bg-neutral-200" />
          </div>

          <Button
            onClick={() => setSsoDialog(true)}
            disabled={loading}
            className="h-11 w-full rounded-none bg-[#2F2F2F] text-white hover:bg-black"
            data-testid="microsoft-sso-button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="mr-2" width="14" height="14" viewBox="0 0 21 21" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            Continue with Microsoft
          </Button>

          <Dialog open={ssoDialog} onOpenChange={setSsoDialog}>
            <DialogContent className="rounded-none">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: '"Work Sans", sans-serif' }}>Microsoft Sign-In</DialogTitle>
                <DialogDescription className="text-xs text-neutral-600">
                  Enter the work email you wish to sign in with. The account must already exist on this platform — admins add users from <span className="font-mono">Settings → Users & Roles</span> first.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Microsoft email</Label>
                <Input
                  value={ssoEmail}
                  onChange={(e) => setSsoEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="rounded-none border-neutral-300"
                  data-testid="sso-email-input"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={submitMicrosoft}
                  className="rounded-none bg-[#2F2F2F] text-white hover:bg-black"
                  data-testid="sso-confirm-button"
                >
                  Continue
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <p className="mt-6 text-center text-sm text-neutral-600">
            New here?{" "}
            <Link to="/register" className="font-medium text-[#002FA7] underline-offset-2 hover:underline" data-testid="login-register-link">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
